import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export interface ExcelColumn<T> {
    header: string;
    key?: keyof T;
    value?: (row: T) => string | number | boolean | null | undefined; // eslint-disable-line no-unused-vars
    width?: number;
    headerBgColor?: string;
}

export const exportToExcelWithFormatter = async <T extends Record<string, unknown>>(
    data: T[],
    columns: Array<ExcelColumn<T>>,
    fileName: string,
    sheetName = 'Sheet1',
    includeDisclaimer = false,
    disclaimer?: [] | { text: string; column: string }[],
    disclaimerSheetName = 'Disclaimer'
) => {
    if (data.length === 0 || columns.length === 0) {
        return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    worksheet.columns = columns.map((col) => ({
        header: col.header,
        key: col.header,
        width: col.width ?? 20
    }));

    data.forEach((row) => {
        const rowData: Record<string, string | number | boolean | null | undefined> = {};

        columns.forEach((column) => {
            if (column.value) {
                rowData[column.header] = column.value(row);
                return;
            }

            if (column.key) {
                rowData[column.header] = row[column.key] as string | number | boolean | null | undefined;
            }
        });

        worksheet.addRow(rowData);
    });

    worksheet.eachRow((row, rowNumber) => {
        row.height = 26.5;
        row.eachCell((cell) => {
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };

            if (rowNumber === 1) {
                const colIndex = Number(cell.col) - 1;
                const bgColor = columns[colIndex]?.headerBgColor;

                if (bgColor) {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: bgColor }
                    };
                }
                cell.font = { name: 'Aptos Narrow', bold: true };
            }
        });
    });

    if (includeDisclaimer && disclaimer) {
        const disclaimerWs = workbook.addWorksheet(disclaimerSheetName);
        const disclaimerColumns = Array.from(new Set(disclaimer.map((d) => d.column)));

        disclaimerWs.columns = disclaimerColumns.map((col) => ({
            header: col,
            key: col,
            width: 20
        }));

        disclaimer.forEach((d) => {
            const splits = d.text.split('\n');
            splits.forEach((text) => {
                disclaimerWs.addRow({ [d.column]: text.trim() });
            });
        });

        disclaimerWs.pageSetup = {
            orientation: 'portrait',
            horizontalCentered: false,
            verticalCentered: false,
            margins: {
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                header: 0,
                footer: 0
            }
        };

        disclaimerWs.views = [
            {
                state: 'normal',
                showGridLines: false
            }
        ];

        disclaimerWs.eachRow((row, rowNumber) => {
            // row.height = 26.5;
            row.eachCell((cell) => {
                cell.border = {};
                if (rowNumber === 1) {
                    cell.font = { color: { argb: 'FF000000' }, bold: true, name: 'Aptos Narrow' };
                } else {
                    cell.font = { italic: true, name: 'Aptos Narrow' };
                }
            });
        });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const dataBlob = new Blob([buffer], { type: 'application/octet-stream' });
    const resolvedFileName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;

    saveAs(dataBlob, resolvedFileName);
};

export default exportToExcelWithFormatter;
